import { Storage } from 'aws-amplify';
import axios from 'axios';
import { Dispatch } from 'redux';
import { errorHandler } from 'services/axiosService';

import { AppThunkDispatch, QContext, REACT_APP_QWEALTH_API_V3 } from '@qwealth/qdata';
import { S3Item } from '@qwealth/qvault';

import { setKypHasError } from 'data/actions/KYP';
import { Kyp, KypFilters } from 'data/models/Kyp';
import { kypFilterDateOptionToDateKeyMap } from 'data/refData/kyp';

export const searchKyps = async (searchQuery: string, dispatch?: Dispatch): Promise<Kyp[]> => {
  try {
    const response = await axios.get(`${REACT_APP_QWEALTH_API_V3}/kyp?name,ticker,assessment[like]=${searchQuery}`);
    return response.data;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, 'API Error: Failed searching KYPs')(e);
    }
    return [];
  }
};

const buildKypParams = (filters: KypFilters | undefined) => {
  const params = filters
    ? Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => {
          return (
            key != 'filteringDate' && // date fields are handled below
            key != 'startDate' &&
            key != 'endDate' &&
            value != null //Filtering dates are default null/undefined
          );
        }),
      )
    : {};

  if (filters?.reviewStatus === 'expired') {
    params['reviewStatus'] = null;
    params['isExpired'] = true;
  }

  const dateFieldToParamName = filters?.filteringDate ? kypFilterDateOptionToDateKeyMap[filters?.filteringDate] : '';

  if (filters?.startDate) {
    params[`${dateFieldToParamName}[startDate]`] = filters.startDate;
  }

  if (filters?.endDate) {
    params[`${dateFieldToParamName}[endDate]`] = filters.endDate;
  }

  return params;
};

export const fetchKyps = async (filters?: KypFilters, dispatch?: AppThunkDispatch): Promise<Kyp[]> => {
  try {
    const params = buildKypParams(filters);
    const response = await axios.get(`${REACT_APP_QWEALTH_API_V3}/kyp`, { params });
    return response.data;
  } catch (e) {
    if (dispatch) {
      dispatch(setKypHasError(true));
      errorHandler(dispatch, 'API Error: Failed Fetching KYPs')(e);
    }
    return [];
  }
};

export const patchKyp = async (kyp: Kyp, dispatch?: Dispatch): Promise<Kyp | undefined> => {
  try {
    const response = await axios.patch(`${REACT_APP_QWEALTH_API_V3}/kyp/${kyp.uuid}`, kyp);
    return response.data;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, `API Error: Failed updating KYP: ${kyp.name}`)(e);
    }
    return undefined;
  }
};

export const postKyp = async (kyp: Kyp, dispatch?: Dispatch): Promise<Kyp | undefined> => {
  try {
    const payload = {
      name: kyp.name,
      type: kyp.type,
      ticker: kyp.ticker,
      partner: kyp.partner,
      isinCode: kyp.isinCode,
      assessment: kyp.assessment,
      assetClass: kyp.assetClass,
      reviewStatus: 'pending',
      isApproved: 0,
    };

    const response = await axios.post(`${REACT_APP_QWEALTH_API_V3}/kyp`, payload);
    return response.data;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, `API Error: Failed posting KYP: ${kyp.name}`)(e);
    }
    return undefined;
  }
};

export const deleteKyp = async (kyp: Kyp, dispatch?: Dispatch): Promise<Kyp | undefined> => {
  try {
    const response = await axios.delete(`${REACT_APP_QWEALTH_API_V3}/kyp/${kyp.uuid}`);
    return response.data;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, `API Error: Failed Deleting KYP: ${kyp.name}`)(e);
    }
    return undefined;
  }
};

export const deleteAllKypFileAttachments = async (attachments: S3Item[], dispatch?: Dispatch) => {
  try {
    attachments.forEach(async attachment => {
      const didDelete = await deleteKypFileAttachment(attachment, dispatch);
      if (!didDelete) {
        throw new Error(`Failed to delete Kyp Attachment: ${attachment.key}`);
      }
    });
    return true;
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, 'API Error: Failed to delete All KYP File Attachments')(e);
    }
    return false;
  }
};

export const deleteKypFileAttachment = async (attachment: S3Item, dispatch?: Dispatch) => {
  try {
    if (attachment.key) {
      await Storage.remove(attachment.key);
      return true;
    } else {
      throw new Error(`No path to attachment. Failed to delete Kyp Attachment: ${attachment.key}`);
    }
  } catch (e) {
    if (dispatch) {
      errorHandler(dispatch, 'API Error: Failed to delete KYP File Attachment')(e);
    }
    return false;
  }
};
